import { checkOnboarding } from '#core/services/OnboardingChecker';
import { defineStore } from 'pinia';
import type { User } from '../types';
import { isNull, isEmpty } from 'lodash';

export const useAuthStore = defineStore('auth', () => {
  const { data } = useAuth();
  const user = ref<User>(data.value?.data || null);
  const getUser = computed(() => user.value || ({} as User));
  const hasLoggedInUser = computed(() => Boolean(user.value));
  const hasCompletedOnboarding = computed(() => !isNull(user.value?.onboarded_at));
  const isVerified = computed(() => user.value?.email_verified);
  const onboardingCompleted = computed(() => checkOnboarding(getUser.value));
  const userRole = computed(() => user.value?.role);
  const hasRole = computed(() => Boolean(user.value?.role));
  const hasSetupProfile = computed(() => Boolean(user.value?.first_name && user.value?.last_name));
  const isPhoneVerified = computed(() => user.value?.phone_number_verified);

  const hasSetupBusiness = computed(() =>
    Boolean(user.value?.business?.name)
    && Boolean(user.value?.business?.abn)
    && Boolean(user.value?.business?.address)
    && user.value?.business?.is_licensed !== null
    && user.value?.business?.is_licensed !== undefined
  );

  watch(
    () => data.value,
    (newValue) => {
      if (newValue?.data) {
        user.value = newValue.data;
      } else {
        // Clear user when logged out
        user.value = null;
        // Clear onboarding form data from localStorage when user logs out
        if (process.client) {
          try {
            localStorage.removeItem('onboarding_setup_account_form');
          } catch (e) {
            console.warn('Failed to clear onboarding form data from localStorage on logout', e);
          }
        }
      }
    },
    { immediate: true }
  );

  const logout = () => {
    user.value = null;
    // Clear onboarding form data from localStorage when explicitly logging out
    if (process.client) {
      try {
        localStorage.removeItem('onboarding_setup_account_form');
      } catch (e) {
        console.warn('Failed to clear onboarding form data from localStorage on logout', e);
      }
    }
  };

  return {
    user,
    getUser,
    hasLoggedInUser,
    hasCompletedOnboarding,
    isVerified,
    onboardingCompleted,
    userRole,
    hasRole,
    hasSetupProfile,
    isPhoneVerified,
    hasSetupBusiness,
    logout,
  };
});
